import { memo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Props } from '@/components/TaskStatus/types';
import { Color } from '@/constants/color';
import inputStyles from '@/constants/inputStyles';
import itemStatuses from '@/constants/itemStatuses';
import { ItemStatus } from '@/types/item';
import Button from '@/ui/components/Button';
import DatetimePicker from '@/ui/components/Form/DatetimePicker';
import Label from '@/ui/components/Form/Label';
import Spinner from '@/ui/components/Spinner';
import itemStatusName from '@/utils/itemStatusName';

const TaskStatus = ({ item, updateItem }: Props) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch
  } = useForm({
    defaultValues: {
      snoozed_until: item.snoozed_until,
      status: item.status
    }
  });
  const values = watch();
  const { isPending, mutate } = updateItem;

  const saveStatus = () => {
    mutate({
      id: item.id,
      values: {
        ...(values.status === 'snoozed' && { snoozed_until: values.snoozed_until }),
        status: values.status as ItemStatus
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(saveStatus)}>
      <div className="mb-2">
        <Label htmlFor="id-select-item-status" text="Status">
          <select
            {...register('status')}
            className={`${inputStyles} ml-2`}
            id="id-select-item-status">
            {itemStatuses.map((option) => {
              return (
                <option key={option} value={option}>
                  {itemStatusName(option)}
                </option>
              );
            })}
          </select>
        </Label>
        {errors.status?.message && <p className="text-red-600">{errors.status.message}</p>}
      </div>

      {values.status === 'snoozed' && (
        <div className="mb-2">
          <Controller
            name="snoozed_until"
            control={control}
            render={({ field }) => {
              return (
                <DatetimePicker
                  disabled={values.status !== 'snoozed'}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  label="Snoozed Until"
                  name={field.name}
                  value={field.value}
                />
              );
            }}
            rules={{
              validate: (value) => {
                return values.status === 'snoozed' && !value
                  ? 'Required when status is snoozed'
                  : true;
              }
            }}
          />
          {errors.snoozed_until?.message && (
            <p className="text-red-600">{errors.snoozed_until.message}</p>
          )}
        </div>
      )}

      <Button color={Color.GREEN} disabled={isPending} type="submit">
        {isPending ? <Spinner /> : 'Save Status'}
      </Button>
    </form>
  );
};

export default memo(TaskStatus);
